import { useEffect, useRef } from "react";
import type { AdsAssistantPostMediaItem } from "@/lib/adsAssistantClient";

export function AdsAssistantPostMediaLightbox(props: {
  open: boolean;
  items: AdsAssistantPostMediaItem[];
  index: number;
  onClose: () => void;
  onIndexChange: (i: number) => void;
}) {
  const { open, items, index, onClose, onIndexChange } = props;
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && items.length > 1) {
        e.preventDefault();
        onIndexChange((index + items.length - 1) % items.length);
      }
      if (e.key === "ArrowRight" && items.length > 1) {
        e.preventDefault();
        onIndexChange((index + 1) % items.length);
      }
    };
    document.addEventListener("keydown", onKey);
    closeBtnRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, onIndexChange, index, items.length]);

  if (!open || !items.length) return null;

  const cur = items[index];
  const url = cur?.signed_url;
  if (!url) return null;
  const isVideoVariant =
    typeof cur?.content_type === "string" && cur.content_type.toLowerCase().startsWith("video/");

  return (
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-black/70 p-3"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ads-post-media-lightbox-title"
        className="relative flex max-h-[92vh] w-full max-w-4xl flex-col rounded-xl border border-gray-600 bg-zinc-950 shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 border-b border-gray-700 px-3 py-2">
          <h2 id="ads-post-media-lightbox-title" className="truncate text-sm font-medium text-gray-100">
            Post image {items.length > 1 ? `${index + 1} / ${items.length}` : ""}
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            className="shrink-0 rounded-lg border border-gray-600 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-gray-100 hover:bg-zinc-800"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div className="relative flex min-h-0 flex-1 items-center justify-center p-2">
          {items.length > 1 ? (
            <button
              type="button"
              className="absolute left-1 z-10 rounded-full border border-gray-600 bg-black/50 px-2 py-3 text-sm text-white hover:bg-black/70"
              aria-label="Previous image"
              onClick={() => onIndexChange((index + items.length - 1) % items.length)}
            >
              ‹
            </button>
          ) : null}
          {isVideoVariant ? (
            <div
              key={cur.id}
              className="flex max-h-[min(80vh,720px)] w-full max-w-2xl flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-gray-600 bg-zinc-900 p-6 text-center text-sm text-gray-200"
            >
              <p className="font-medium">This is a video.</p>
              <p className="text-xs text-gray-400">
                In-app playback is not available — open the post on its platform to play the video.
              </p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex rounded-lg border border-gray-500 bg-zinc-800 px-3 py-1.5 text-xs font-medium text-gray-100 hover:bg-zinc-700"
              >
                Open on platform
              </a>
            </div>
          ) : (
            <img
              key={cur.id}
              src={url}
              alt=""
              className="max-h-[min(80vh,720px)] w-auto max-w-full object-contain"
              decoding="async"
            />
          )}
          {items.length > 1 ? (
            <button
              type="button"
              className="absolute right-1 z-10 rounded-full border border-gray-600 bg-black/50 px-2 py-3 text-sm text-white hover:bg-black/70"
              aria-label="Next image"
              onClick={() => onIndexChange((index + 1) % items.length)}
            >
              ›
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
